import { SEC_AUTH_M_TK } from 'constants/AuthConstant';
import { AUTH_PREFIX_PATH } from 'configs/AppConfig';
import { v4 as uuidv4 } from 'uuid';

/** Canonical OAuth error_code values from POST /auth/google and POST /auth/microsoft */
export const OAUTH_ERROR_CODES = {
	NO_ACCOUNT_FOUND: 'no_account_found',
	ACCOUNT_EXISTS: 'account_exists',
	EMAIL_IN_USE: 'email_in_use',
	ACCOUNT_DEACTIVATED: 'account_deactivated',
	ACCOUNT_REJECTED: 'account_rejected',
	INVALID_TOKEN: 'invalid_token',
	EMAIL_NOT_VERIFIED: 'email_not_verified',
	DOMAIN_BLOCKED: 'domain_blocked',
	SERVER_ERROR: 'server_error',
	VERIFICATION_UNAVAILABLE: 'verification_unavailable',
};

/** @deprecated Use OAUTH_ERROR_CODES — kept for existing imports */
export const GOOGLE_AUTH_ERRORS = {
	NO_ACCOUNT_FOUND: OAUTH_ERROR_CODES.NO_ACCOUNT_FOUND,
	ACCOUNT_EXISTS: OAUTH_ERROR_CODES.ACCOUNT_EXISTS,
	EMAIL_IN_USE: OAUTH_ERROR_CODES.EMAIL_IN_USE,
};

/** Errors handled inline on OAuth forms (suppress generic Redux alert) */
export const OAUTH_UI_HANDLED_ERRORS = new Set(Object.values(OAUTH_ERROR_CODES));

/** Build POST /auth/google|microsoft body — login sends id_token only; signup may include extra fields. */
export const buildOAuthRequestBody = (idToken, mode = 'login', signupFields = {}) => {
	const body = { id_token: idToken, mode };
	if (mode === 'signup' && signupFields && typeof signupFields === 'object') {
		Object.assign(body, signupFields);
	}
	return body;
};

const readOAuthErrorBody = (data) => {
	if (!data || typeof data !== 'object') {
		return { errorCode: '', message: '' };
	}

	if (data.success === false) {
		return {
			errorCode: String(data.error_code || '').trim(),
			message: String(data.message || '').trim(),
		};
	}

	const detail = data.detail;
	if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
		return {
			errorCode: String(detail.error_code || detail.error || '').trim(),
			message: String(detail.message || '').trim(),
		};
	}
	if (typeof detail === 'string') {
		return { errorCode: '', message: detail.trim() };
	}

	return {
		errorCode: String(data.error_code || '').trim(),
		message: String(data.message || '').trim(),
	};
};

const oauthGenericFallback = (flow = 'login') =>
	flow === 'signup' ? 'Sign-up failed. Please try again.' : 'Sign-in failed. Please try again.';

/** Prefer backend message; use fallback only when the response has no message. */
const oauthAlertMessage = (bodyMessage, fallback) => {
	const trimmed = String(bodyMessage || '').trim();
	return trimmed || fallback;
};

/** Optional navigation links for known OAuth error codes (login/signup screens). */
const getOAuthLinkExtras = (errorCode) => {
	if (errorCode === OAUTH_ERROR_CODES.NO_ACCOUNT_FOUND) {
		return {
			linkPath: `${AUTH_PREFIX_PATH}/register-1`,
			linkLabel: 'Sign up instead',
		};
	}
	if (
		errorCode === OAUTH_ERROR_CODES.ACCOUNT_EXISTS ||
		errorCode === OAUTH_ERROR_CODES.EMAIL_IN_USE
	) {
		return {
			linkPath: `${AUTH_PREFIX_PATH}/login`,
			linkLabel: 'Log in instead',
		};
	}
	return {};
};

export const parseOAuthErrorPayload = (data, status, fallbackMessage = '') => {
	const { errorCode, message: bodyMessage } = readOAuthErrorBody(data);
	return {
		status,
		errorCode,
		message: oauthAlertMessage(bodyMessage, fallbackMessage),
		rawResponse: data,
	};
};

export const parseOAuthAuthError = (err, fallbackMessage = 'Authentication failed') => {
	const status = err?.response?.status ?? err?.status ?? 0;
	const data = err?.response?.data;
	const parsed = parseOAuthErrorPayload(data, status, '');

	if (parsed.message) {
		return parsed;
	}

	if (err?.isNetworkError) {
		return {
			...parsed,
			message: err?.message || 'Network Error',
		};
	}

	return {
		...parsed,
		message: fallbackMessage,
	};
};

export const parseGoogleAuthError = (err) =>
	parseOAuthAuthError(err, 'Google authentication failed');

/**
 * Google OAuth error display — explicit HTTP 403 + error_code branching.
 * Other statuses delegate to resolveOAuthErrorDisplay.
 */
export const resolveGoogleOAuthErrorDisplay = ({
	status,
	errorCode,
	message,
	flow = 'login',
	rawResponse,
}) => {
	const bodyMessage = message || '';

	if (status === 403) {
		if (errorCode === OAUTH_ERROR_CODES.ACCOUNT_DEACTIVATED) {
			return {
				action: 'inline',
				alertMessage: oauthAlertMessage(bodyMessage, oauthGenericFallback(flow)),
			};
		}
		if (errorCode === OAUTH_ERROR_CODES.ACCOUNT_REJECTED) {
			return {
				action: 'inline',
				alertMessage: oauthAlertMessage(bodyMessage, oauthGenericFallback(flow)),
			};
		}
		if (errorCode === OAUTH_ERROR_CODES.DOMAIN_BLOCKED) {
			return {
				action: 'inline',
				alertMessage: oauthAlertMessage(bodyMessage, oauthGenericFallback(flow)),
			};
		}
		return {
			action: 'inline',
			alertMessage: oauthAlertMessage(
				bodyMessage,
				'Access denied. Please contact your administrator.'
			),
		};
	}

	return resolveOAuthErrorDisplay({
		status,
		errorCode,
		message,
		provider: 'Google',
		flow,
		rawResponse,
	});
};

/**
 * Route OAuth API errors using HTTP status + error_code (A-F1).
 * Returns display instructions for an inline Alert.
 */
export const resolveOAuthErrorDisplay = ({
	status,
	errorCode,
	message,
	provider = 'Google',
	flow = 'login',
	rawResponse,
}) => {
	const bodyMessage = message || '';
	const genericFallback = oauthGenericFallback(flow);

	if (status === 401 && errorCode === OAUTH_ERROR_CODES.NO_ACCOUNT_FOUND) {
		return {
			action: 'inline',
			alertMessage: oauthAlertMessage(bodyMessage, genericFallback),
			linkPath: `${AUTH_PREFIX_PATH}/register-1`,
			linkLabel: 'Sign up instead',
		};
	}

	if (status === 409 && errorCode === OAUTH_ERROR_CODES.ACCOUNT_EXISTS) {
		return {
			action: 'inline',
			alertMessage: oauthAlertMessage(bodyMessage, genericFallback),
			linkPath: `${AUTH_PREFIX_PATH}/login`,
			linkLabel: 'Log in instead',
		};
	}

	if (status === 409 && errorCode === OAUTH_ERROR_CODES.EMAIL_IN_USE) {
		return {
			action: 'inline',
			alertMessage: oauthAlertMessage(bodyMessage, genericFallback),
			linkPath: `${AUTH_PREFIX_PATH}/login`,
			linkLabel: 'Log in instead',
		};
	}

	if (status === 403 && errorCode === OAUTH_ERROR_CODES.ACCOUNT_DEACTIVATED) {
		return {
			action: 'inline',
			alertMessage: oauthAlertMessage(bodyMessage, genericFallback),
		};
	}

	if (status === 403 && errorCode === OAUTH_ERROR_CODES.ACCOUNT_REJECTED) {
		return {
			action: 'inline',
			alertMessage: oauthAlertMessage(bodyMessage, genericFallback),
		};
	}

	if (status === 401 && errorCode === OAUTH_ERROR_CODES.INVALID_TOKEN) {
		return {
			action: 'inline',
			alertMessage: oauthAlertMessage(
				bodyMessage,
				`Something went wrong with ${provider} sign-in. Please try again.`
			),
			showRetry: true,
		};
	}

	if (status === 422 && errorCode === OAUTH_ERROR_CODES.EMAIL_NOT_VERIFIED) {
		return {
			action: 'inline',
			alertMessage: oauthAlertMessage(bodyMessage, genericFallback),
		};
	}

	if (status === 403 && errorCode === OAUTH_ERROR_CODES.DOMAIN_BLOCKED) {
		return {
			action: 'inline',
			alertMessage: oauthAlertMessage(bodyMessage, genericFallback),
		};
	}

	if (status === 503 || errorCode === OAUTH_ERROR_CODES.VERIFICATION_UNAVAILABLE) {
		return {
			action: 'inline',
			alertMessage: oauthAlertMessage(
				bodyMessage,
				`${provider} sign-in is temporarily unavailable. Please try again in a moment.`
			),
		};
	}

	if (status === 500 || errorCode === OAUTH_ERROR_CODES.SERVER_ERROR) {
		return {
			action: 'inline',
			alertMessage: oauthAlertMessage(bodyMessage, 'Something went wrong. Please try again.'),
		};
	}

	if (bodyMessage) {
		return {
			action: 'inline',
			alertMessage: bodyMessage,
			...getOAuthLinkExtras(errorCode),
		};
	}

	console.error(`[OAuth ${provider}] Unhandled error:`, {
		status,
		errorCode,
		message: bodyMessage,
		rawResponse,
	});

	return {
		action: 'inline',
		alertMessage: genericFallback,
	};
};

/** Handle a rejected googleAuth / microsoftAuth thunk result in login or signup forms */
export const processOAuthRejectedAction = (action, thunk, {
	provider,
	flow,
	setOAuthError,
	hideAuthMessage,
}) => {
	if (!thunk.rejected.match(action)) {
		return false;
	}

	const payload = action.payload || {};
	const display = provider === 'Google'
		? resolveGoogleOAuthErrorDisplay({
			status: payload.status ?? 0,
			errorCode: payload.errorCode ?? '',
			message: payload.message ?? '',
			flow,
			rawResponse: payload.rawResponse,
		})
		: resolveOAuthErrorDisplay({
			status: payload.status ?? 0,
			errorCode: payload.errorCode ?? '',
			message: payload.message ?? '',
			provider,
			flow,
			rawResponse: payload.rawResponse,
		});

	hideAuthMessage?.();

	setOAuthError(display);
	return true;
};

export const isOAuthSuccessResponse = (data) => {
	if (!data || typeof data !== 'object') {
		return false;
	}
	if (data.success === false) {
		return false;
	}
	return Boolean(data.session_token || data.token);
};

export const persistGoogleAuthSession = (data) => {
	if (!isOAuthSuccessResponse(data)) {
		return null;
	}

	const token = data.session_token || data.token;
	const role = data.user?.role || data.role;
	const email = (data.user?.email || data.email || data.username || '').trim().toLowerCase();
	const isNewUser = Boolean(data.is_new_user);

	localStorage.setItem(SEC_AUTH_M_TK, token);
	if (role) {
		localStorage.setItem('design', role);
	}
	if (email) {
		localStorage.setItem('_un', email);
		localStorage.setItem('convKey', `UI_${email}_${uuidv4()}`);
	}

	return { token, role, email, isNewUser, message: data.message || '' };
};
